// TODO:
// remove moment once e3dc core date picker change moment to date-fns

import React from 'react'
import { Grid } from '@mui/material'
import { DatePicker, TextField } from '@e3dc-react/shell/Elements/InputFields'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Controller, useFormContext } from 'react-hook-form'
import { EditTariffFormData } from '../../models/tariffs-form-model'
import { currencyCodeToSymbol } from 'src/Libs/e-mobility/shared'
import moment from '@e3dc-react/shell/Libs/moment'

/**
 * Component with form fields for edit static tariff
 * It requires formik context for the input fields
 */
export const EditStaticTariffForm = (): JSX.Element => {
  const { t } = useCustomTranslation()
  const {
    control,
    watch,
    formState: { errors },
    trigger,
  } = useFormContext<EditTariffFormData>()

  const currencySymbol = currencyCodeToSymbol(watch('currencyCode'))

  return (
    <Grid container spacing={5}>
      <Grid item xs={12} mt={1}>
        <Controller
          name="tariffName"
          control={control}
          render={({ field }) => (
            <TextField {...field} label={t('emobility.tariffs.tariffName')} helperText={errors.tariffName?.message} required clearable={false} />
          )}
        />
      </Grid>

      <Grid item xs={12}>
        <Controller
          name="tariffGridPrice"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              type="number"
              step={0.1}
              label={t('emobility.tariffs.grid-price-field', { currencySymbol, context: currencySymbol ? 'currency-symbol' : undefined })}
              helperText={errors.tariffGridPrice?.message}
              required
              clearable={false}
            />
          )}
        />
      </Grid>

      <Grid item xs={12}>
        <Controller
          control={control}
          name="tariffSolarPrice"
          render={({ field: { value, onChange, onBlur } }) => (
            <TextField
              type="number"
              step={0.1}
              label={t('emobility.tariffs.solar-price-field', { currencySymbol, context: currencySymbol ? 'currency-symbol' : undefined })}
              helperText={errors.tariffSolarPrice?.message}
              value={value ?? undefined}
              onBlur={onBlur}
              onChange={onChange}
            />
          )}
        />
      </Grid>

      <Grid item xs={12}>
        <Controller
          name="tariffDischargePrice"
          control={control}
          render={({ field: { value, onChange, onBlur } }) => (
            <TextField
              type="number"
              step={0.1}
              label={t('emobility.tariffs.discharge-price-field', { currencySymbol, context: currencySymbol ? 'currency-symbol' : undefined })}
              value={value ?? undefined}
              helperText={errors.tariffDischargePrice?.message}
              onBlur={onBlur}
              onChange={onChange}
            />
          )}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <Controller
          control={control}
          name="startDate"
          render={({ field: { onChange, value } }) => (
            <DatePicker
              value={moment(value)}
              label={t('emobility.tariffs.startDate')}
              helperText={errors.startDate?.message}
              onChange={m => {
                /**
                 * Why this onChange logic looks more complex than expected:
                 *
                 * - The DatePicker intentionally works with `moment` values (not raw text input),
                 *   which is convenient for most cases. However, when the user types an incomplete
                 *   or malformed date, the component still emits a `moment` instance.
                 *
                 * - In those cases, `m.isValid()` becomes the only reliable way to detect that
                 *   the input is not a valid date. We do NOT receive the original string, so we
                 *   cannot validate it directly.
                 *
                 * - Without this handling, invalid or partial inputs (e.g. "11/11/111") would
                 *   either be treated as valid dates or silently cleared by the DatePicker,
                 *   leading to inconsistent form state and validation issues.
                 *
                 * - To keep form behavior predictable, we map the result explicitly:
                 *   - `null` → user cleared the input
                 *   - invalid date → '' (treated as invalid in the form)
                 *   - valid date → ISO string (normalized to start/end of day)
                 *
                 * In short: this ensures that partial/invalid user input is not lost and is
                 * correctly reflected in form validation, despite the DatePicker not exposing
                 * raw input values.
                 *
                 * The same goes for handling "endDate"
                 */
                let nextValue: string | null = null

                if (m) {
                  nextValue = m.isValid() ? m.startOf('day').toISOString() : ''
                }

                onChange(nextValue)
                trigger('endDate')
              }}
            />
          )}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <Controller
          name="endDate"
          control={control}
          render={({ field: { value, onChange } }) => (
            <DatePicker
              value={moment(value)}
              onChange={m => {
                if (!m) {
                  return onChange(null)
                }

                if (!m.isValid()) {
                  return onChange('')
                }

                onChange(m.endOf('day').toISOString())
              }}
              label={t('emobility.tariffs.endDate')}
              helperText={errors.endDate?.message}
            />
          )}
        />
      </Grid>
    </Grid>
  )
}
